import { weekStartUTC } from "@shared/workweek";

/**
 * Initial week offset from a `?week=YYYY-MM-DD` or `?d=YYYY-MM-DD` URL param.
 * Used by Home and Drafts so a deep link (notification, or a tapped day in the
 * week strip) opens on the right week. Whole-week granularity (Sunday-based
 * delta) — timezone-agnostic, and NOT a sensitive computation: it only chooses
 * which week to ASK the server about; the server still classifies everything.
 */
export function initialWeekOffsetFromUrl(): number {
  if (typeof window === "undefined") return 0;
  const m = /[?&](?:week|d)=(\d{4}-\d{2}-\d{2})/.exec(window.location.search);
  if (!m) return 0;
  const target = new Date(m[1] + "T00:00:00.000Z");
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const thisWeekStart = new Date(today);
  thisWeekStart.setUTCDate(today.getUTCDate() - today.getUTCDay());
  // floor, NOT round: a date 4–6 days into the current week is still THIS week
  // (offset 0). Math.round(5/7)=1 would wrongly jump to next week — the bug
  // behind "Draft today on a Friday opened the wrong week".
  return Math.floor((target.getTime() - thisWeekStart.getTime()) / (7 * 86_400_000));
}

/**
 * The raw `?week=` / `?d=` target date at mount, unparsed. Pages that honor a
 * non-Sunday `weekStartDay` preference must NOT trust the Sunday-based offset
 * above alone: a week-start key written under weekStartDay=1 (Monday) decodes
 * one week early whenever todayUTCDay < weekStartDay (the floor lands in the
 * previous Sunday-week). Capture this once, then derive the exact offset with
 * `weekOffsetForTarget` when the preference is known.
 */
export function initialWeekTargetFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const m = /[?&](?:week|d)=(\d{4}-\d{2}-\d{2})/.exec(window.location.search);
  return m ? m[1] : null;
}

/**
 * EXACT whole-week offset from the current week to the target date, both
 * normalized to the SAME `weekStartDay` base — the drift-free decode for
 * Team/TeamReport round-trips. Pass the page's own `now` anchor so the
 * offset math shares its base with the page's weekStart derivation.
 */
export function weekOffsetForTarget(
  targetKey: string | null,
  weekStartDay: number,
  now: Date = new Date(),
): number {
  if (!targetKey || !/^\d{4}-\d{2}-\d{2}$/.test(targetKey)) return 0;
  const target = new Date(targetKey + "T00:00:00.000Z");
  if (Number.isNaN(target.getTime())) return 0;
  const base = weekStartUTC(now, weekStartDay);
  const targetStart = weekStartUTC(target, weekStartDay);
  return Math.round((targetStart.getTime() - base.getTime()) / (7 * 86_400_000));
}

/**
 * Keep `?week=YYYY-MM-DD` in the address bar in sync with the viewed week.
 * replaceState only — no navigation, no history entry, no wouter re-render.
 * It exists so the URL a user LEAVES from always carries the week they were
 * actually looking at: in-app Back links, browser back/forward, refresh, and
 * copied links all land on the same week (the "Back returned to the current
 * week" bug). Clears a stale `?d=` alias so the two params can't disagree.
 */
export function syncWeekUrlParam(weekStartKey: string): void {
  if (typeof window === "undefined") return;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStartKey)) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("week") === weekStartKey && !url.searchParams.has("d")) return;
  url.searchParams.set("week", weekStartKey);
  url.searchParams.delete("d");
  window.history.replaceState(window.history.state, "", url);
}
